import { useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import { APPS, appKey, displayUrl, languageTag, type AppEntry } from "../registry/apps";
import { ZONE } from "../registry/constants";
import { isPlaceholder } from "../lib/progress";
import { formatDay, formatInt } from "../lib/format";
import { sheetShouldClose } from "../lib/gestures";

/** Registry row target: the surface-qualified path door (redirects to the app). */
export function rowUrl(app: Pick<AppEntry, "surface" | "slug">): string {
  return `https://${ZONE}/${app.surface}/${app.slug}`;
}

// App numbers come from the full registry, independent of search results.
const appNumbers = new Map(APPS.map((app, i) => [appKey(app), i + 1]));

type InventoryPanelProps = {
  apps: readonly AppEntry[];
  open: boolean;
  hotkeys?: boolean;
  onOpen: () => void;
  onClose: () => void;
};

export function InventoryPanel({
  apps,
  open,
  hotkeys = true,
  onOpen,
  onClose,
}: InventoryPanelProps) {
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const sheetRef = useRef<HTMLElement>(null);
  const dragYRef = useRef(0);
  const startYRef = useRef(0);
  const draggingRef = useRef(false);

  useEffect(() => {
    if (!hotkeys) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && open) {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== "/" || event.metaKey || event.ctrlKey || event.altKey) return;
      const t = event.target;
      if (t instanceof HTMLInputElement || t instanceof HTMLTextAreaElement) return;
      event.preventDefault();
      onOpen();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [hotkeys, open, onOpen, onClose]);

  useEffect(() => {
    if (!open) return;
    const coarse =
      typeof window !== "undefined" && window.matchMedia("(pointer: coarse)").matches;
    if (coarse) closeRef.current?.focus();
    else inputRef.current?.focus();

    const onDoc = (event: KeyboardEvent) => {
      if (event.key !== "Tab" || !open) return;
      const root = document.getElementById("inventory");
      if (!root) return;
      const focusable = [...root.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      )].filter((el) => el.getClientRects().length > 0 && !el.hasAttribute("disabled"));
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };
    window.addEventListener("keydown", onDoc);
    return () => window.removeEventListener("keydown", onDoc);
  }, [open]);

  useEffect(() => {
    if (open && sheetRef.current) sheetRef.current.style.transform = "";
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return apps.filter((app) => {
      if (!q) return true;
      const blob = `${app.slug} ${app.surface} ${app.title} ${displayUrl(app)} ${app.blurb ?? ""} ${app.language ?? ""}`.toLowerCase();
      return blob.includes(q);
    });
  }, [apps, query]);

  const onHandleDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    draggingRef.current = true;
    startYRef.current = event.clientY;
    dragYRef.current = 0;
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const onHandleMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return;
    const dy = Math.max(0, event.clientY - startYRef.current);
    dragYRef.current = dy;
    if (sheetRef.current) sheetRef.current.style.transform = `translateY(${dy}px)`;
  };

  const onHandleUp = () => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    if (sheetShouldClose(dragYRef.current)) {
      onClose();
      return;
    }
    if (sheetRef.current) sheetRef.current.style.transform = "";
  };

  if (!open) return null;

  return (
    <div className="inventory-back" onClick={onClose}>
      <section
        ref={sheetRef}
        className="inventory"
        id="inventory"
        role="dialog"
        aria-modal="true"
        aria-labelledby="inventory-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="sheet-handle"
          aria-hidden="true"
          onPointerDown={onHandleDown}
          onPointerMove={onHandleMove}
          onPointerUp={onHandleUp}
          onPointerCancel={onHandleUp}
        />

        <header className="dir-head">
          <div className="dir-head-top">
            <div>
              <p className="dir-kicker">App directory</p>
              <h2 id="inventory-title">Registry index</h2>
              <p className="dir-sub" role="status" aria-atomic="true">
                {query.trim() ? `${formatInt(filtered.length)} of ` : ""}
                {formatInt(apps.length)} {apps.length === 1 ? "app" : "apps"}
              </p>
            </div>
            <button
              ref={closeRef}
              type="button"
              className="inventory-close"
              onClick={onClose}
              aria-label="Close registry index"
            >
              <span aria-hidden="true">✕</span> CLOSE <kbd aria-hidden="true">ESC</kbd>
            </button>
          </div>

          <div className="dir-controls">
            <label className="find">
              <span>SEARCH</span>
              <input
                ref={inputRef}
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Name or keyword…"
                aria-label="Search apps"
                autoComplete="off"
                spellCheck={false}
              />
            </label>
          </div>
        </header>

        <div className="dir-scroll">
          <table className="dir-table">
            <colgroup>
              <col className="c-num" />
              <col className="c-surface" />
              <col className="c-slug" />
              <col />
              <col className="c-shipped" />
              <col className="c-open" />
            </colgroup>
            <thead>
              <tr>
                <th scope="col">App #</th>
                <th scope="col">Type</th>
                <th scope="col">Slug</th>
                <th scope="col">App</th>
                <th scope="col">Shipped</th>
                <th scope="col">
                  <span className="sr-only">Open</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="dir-empty">
                    No apps found. Try another name or keyword.
                  </td>
                </tr>
              ) : (
                filtered.map((app) => {
                  const staged = isPlaceholder(app);
                  const href = rowUrl(app);
                  const tag = languageTag(app.language);
                  return (
                    <tr key={appKey(app)} className={staged ? "dir-row is-staged" : "dir-row"}>
                      <td className="num">{appNumbers.get(appKey(app)) ?? "—"}</td>
                      <td className="surface">{app.surface}</td>
                      <td className="slug">{app.slug}</td>
                      <td className="title">
                        <span className="dir-title-row">
                          <a
                            className="dir-row-link dir-title"
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={href}
                          >
                            {app.title}
                          </a>
                          {tag ? <span className="pill lang">{tag}</span> : null}
                          {staged ? (
                            <span className="pill warn">{app.example ? "Example" : "In progress"}</span>
                          ) : null}
                        </span>
                        {app.blurb ? <span className="dir-blurb">{app.blurb}</span> : null}
                      </td>
                      <td className="mono">{formatDay(app.shippedAt)}</td>
                      <td className="open">
                        <a
                          className="dir-open"
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={`Open ${app.title}`}
                        >
                          OPEN ↗
                        </a>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>

          <ul className="dir-list">
            {filtered.length === 0 ? (
              <li className="dir-empty">No apps found. Try another name or keyword.</li>
            ) : (
              filtered.map((app) => {
                const staged = isPlaceholder(app);
                const href = rowUrl(app);
                const tag = languageTag(app.language);
                return (
                  <li key={appKey(app)} className={staged ? "dir-row is-staged" : "dir-row"}>
                    <div className="dir-card-main">
                      <div className="dir-card-top">
                        <span className="num">#{appNumbers.get(appKey(app)) ?? "—"}</span>
                        {staged ? (
                          <span className="pill warn">{app.example ? "Example" : "In progress"}</span>
                        ) : null}
                        {tag ? <span className="pill lang">{tag}</span> : null}
                        <span className="surface">{app.surface}</span>
                        <span className="slug">{app.slug}</span>
                        <span className="dir-shipped">{formatDay(app.shippedAt)}</span>
                      </div>
                      <a
                        className="dir-row-link dir-title"
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {app.title}
                      </a>
                      {app.blurb ? <span className="dir-blurb">{app.blurb}</span> : null}
                    </div>
                    <a
                      className="dir-open"
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Open ${app.title}`}
                    >
                      OPEN ↗
                    </a>
                  </li>
                );
              })
            )}
          </ul>
        </div>
      </section>
    </div>
  );
}
